import { RULE_CATEGORIES, RULE_SOURCE, RULE_STATUS, RULE_TYPES } from './ruleSchema.js';

const now = () => new Date().toISOString();
const builtIn = (rule) => ({
  status: RULE_STATUS.ACTIVE,
  source: RULE_SOURCE.BUILT_IN,
  severity: 'medium',
  createdAt: now(),
  updatedAt: now(),
  ...rule,
});

function canonicalActivities(dataset) {
  return [...new Set((Array.isArray(dataset?.events) ? dataset.events : [])
    .map(e => String(e?.activity || '').trim())
    .filter(Boolean))];
}

function findActivity(activities, candidates) {
  const lowered = activities.map(value => ({ value, lower: value.toLowerCase() }));
  for (const candidate of candidates) {
    const target = String(candidate).toLowerCase();
    const exact = lowered.find(item => item.lower === target);
    if (exact) return exact.value;
  }
  for (const candidate of candidates) {
    const target = String(candidate).toLowerCase();
    const partial = lowered.find(item => item.lower.includes(target) || target.includes(item.lower));
    if (partial) return partial.value;
  }
  return null;
}

function distinctPair(a, b) {
  return a && b && String(a).toLowerCase() !== String(b).toLowerCase();
}

export function createStarterRules({ dataset, model } = {}) {
  const process = String(model?.processType || model?.process_type || dataset?.model?.processType || 'process').trim();
  const activities = canonicalActivities(dataset);
  const definitions = [
    builtIn({
      id: 'perf-rework-rate',
      category: RULE_CATEGORIES.PERFORMANCE,
      ruleType: RULE_TYPES.METRIC_THRESHOLD,
      name: 'Rework rate',
      description: 'Flags a high proportion of cases that repeat an activity.',
      metric: 'rework_rate', operator: '>', threshold: 0.20, unit: 'percent', severity: 'high',
      rationale: 'Repeated activities commonly indicate avoidable loops, failed hand-offs or incomplete resolution.',
    }),
    builtIn({
      id: 'perf-avg-transitions',
      category: RULE_CATEGORIES.PERFORMANCE,
      ruleType: RULE_TYPES.METRIC_THRESHOLD,
      name: 'Average transitions per case',
      description: 'Flags processes with a high number of state or activity transitions per case.',
      metric: 'avg_transitions', operator: '>', threshold: 6, unit: 'number', severity: 'medium',
      rationale: 'High transition counts can indicate excessive routing, hand-offs or process complexity.',
    }),
    builtIn({
      id: 'perf-cycle-variability',
      category: RULE_CATEGORIES.PERFORMANCE,
      ruleType: RULE_TYPES.METRIC_RATIO_THRESHOLD,
      name: 'Cycle-time variability',
      description: 'Compares cycle-time standard deviation with average cycle time.',
      numeratorMetric: 'std_deviation', denominatorMetric: 'cycle_time', operator: '>', threshold: 0.75, unit: 'ratio', severity: 'medium',
      rationale: 'High variability suggests inconsistent execution even when average performance appears acceptable.',
    }),
    builtIn({
      id: 'perf-variant-complexity',
      category: RULE_CATEGORIES.PERFORMANCE,
      ruleType: RULE_TYPES.METRIC_RATIO_THRESHOLD,
      name: 'Variant complexity',
      description: 'Flags a high number of unique variants relative to process volume.',
      numeratorMetric: 'variants', denominatorMetric: 'work_items', operator: '>', threshold: 0.20, unit: 'ratio', severity: 'medium',
      rationale: 'A high variant-to-case ratio can indicate fragmentation, inconsistent handling or over-customisation.',
    }),
  ];

  // Compliance rules are intentionally process controls, not data-quality checks.
  // They are only created where the observed activity vocabulary supports a credible rule.
  const start = findActivity(activities, ['new', 'open', 'to do', 'todo', 'backlog', 'created']);
  const active = findActivity(activities, ['in progress', 'work in progress', 'working', 'active', 'investigation', 'implementation']);
  const resolved = findActivity(activities, ['resolved', 'resolution']);
  const closed = findActivity(activities, ['closed', 'done', 'completed', 'complete']);
  const terminal = resolved || closed;

  if (distinctPair(active, terminal)) definitions.push(builtIn({
    id: 'comp-work-before-completion',
    category: RULE_CATEGORIES.COMPLIANCE,
    ruleType: RULE_TYPES.REQUIRED_SEQUENCE,
    name: 'Work stage before completion',
    description: `Checks that ${process} cases pass through “${active}” before “${terminal}”.`,
    fromActivity: active, toActivity: terminal, operator: '<', threshold: 0.90, unit: 'percent', severity: 'high',
    rationale: 'Cases should normally enter an active work stage before they are completed; skipping that stage can indicate bypassed process controls.',
  }));

  if (distinctPair(resolved, closed)) definitions.push(builtIn({
    id: 'comp-resolved-before-closed',
    category: RULE_CATEGORIES.COMPLIANCE,
    ruleType: RULE_TYPES.REQUIRED_SEQUENCE,
    name: 'Resolution before closure',
    description: `Checks that ${process} cases reach “${resolved}” before “${closed}”.`,
    fromActivity: resolved, toActivity: closed, operator: '<', threshold: 0.95, unit: 'percent', severity: 'high',
    rationale: 'A resolution stage before final closure provides a controlled hand-off between completing the work and formally closing the case.',
  }));

  if (distinctPair(start, terminal)) definitions.push(builtIn({
    id: 'comp-no-direct-start-to-completion',
    category: RULE_CATEGORIES.COMPLIANCE,
    ruleType: RULE_TYPES.FORBIDDEN_TRANSITION,
    name: 'No direct start-to-completion transition',
    description: `Flags ${process} cases that move directly from “${start}” to “${terminal}”.`,
    fromActivity: start, toActivity: terminal, operator: '>', threshold: 0, unit: 'percent', severity: 'high',
    rationale: 'Moving directly from the initial state to completion can bypass required handling, investigation or execution steps.',
  }));

  return definitions;
}
